import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { ProviderMetric } from "@/lib/maintenance-analytics";

interface ProviderPerformanceComparisonProps {
  providers: ProviderMetric[];
}

export function ProviderPerformanceComparison({ providers }: ProviderPerformanceComparisonProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getCompletionRateBadge = (rate: number) => {
    if (rate >= 95) {
      return <Badge variant="default" className="bg-success text-white">Excelente</Badge>;
    } else if (rate >= 85) {
      return <Badge variant="default">Bom</Badge>;
    } else if (rate >= 70) {
      return <Badge className="bg-warning text-white">Regular</Badge>;
    }
    return <Badge variant="destructive">Baixo</Badge>;
  };

  const getRepairTimeBadge = (days: number) => {
    if (days <= 2) {
      return <Badge variant="default" className="bg-success text-white">Rápido</Badge>;
    } else if (days <= 4) {
      return <Badge variant="default">Normal</Badge>;
    } else if (days <= 7) {
      return <Badge className="bg-warning text-white">Lento</Badge>;
    }
    return <Badge variant="destructive">Muito Lento</Badge>;
  };

  // Calculate averages for comparison
  const avgCompletionRate = providers.reduce((sum, p) => sum + p.completionRate, 0) / providers.length;
  const avgRepairDays = providers.reduce((sum, p) => sum + p.avgRepairDays, 0) / providers.length;
  const avgCost = providers.reduce((sum, p) => sum + p.avgCost, 0) / providers.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Comparação de Performance dos Fornecedores</CardTitle>
          <CardDescription>
            Análise detalhada de {providers.length} fornecedores
          </CardDescription>
        </CardHeader>
        <CardContent>
          {providers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-right">Total de OS</TableHead>
                  <TableHead className="text-right">Custo Total</TableHead>
                  <TableHead className="text-right">Custo Médio</TableHead>
                  <TableHead className="text-center">Taxa Conclusão</TableHead>
                  <TableHead className="text-center">Tempo Médio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map((provider, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{provider.providerName}</TableCell>
                    <TableCell className="text-right">{provider.totalOrders}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(provider.totalCost)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {formatCurrency(provider.avgCost)}
                        {provider.avgCost < avgCost ? (
                          <TrendingUp className="h-4 w-4 text-success" />
                        ) : provider.avgCost > avgCost ? (
                          <TrendingDown className="h-4 w-4 text-destructive" />
                        ) : (
                          <Minus className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm">{provider.completionRate.toFixed(1)}%</span>
                        {getCompletionRateBadge(provider.completionRate)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm">{provider.avgRepairDays.toFixed(1)} dias</span>
                        {getRepairTimeBadge(provider.avgRepairDays)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nenhum fornecedor encontrado
            </p>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {providers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Médias Gerais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Taxa de Conclusão Média</p>
                <p className="text-2xl font-bold">{avgCompletionRate.toFixed(1)}%</p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Tempo Médio de Reparo</p>
                <p className="text-2xl font-bold">{avgRepairDays.toFixed(1)} dias</p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Custo Médio por OS</p>
                <p className="text-2xl font-bold">{formatCurrency(avgCost)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
